import { CreditCard } from "@/api/credit-cards/credit-cards";
import { TransactionPreviewResponse } from "@/api/transactions";
import { Button } from "@/components/ui/button/button";
import { buttonVariants } from "@/components/ui/button/button-variants";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form/form";
import PaymentCard from "@/components/ui/paymentCard";
import { currencyFormatter, getTermsOfServiceUrl } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DepositHeader from "../../_components/DepositHeader";

interface SubmitPageProps {
	amount: number;
	onSubmit: () => void;
	selectedCard: CreditCard;
	onBack: () => void;
	promo: string;
	transactionPreview: TransactionPreviewResponse;
}

const submitSchema = z.object({
	termsAccepted: z.boolean().refine((val) => val === true, {
		message: "You must accept the terms and conditions",
	}),
});

type SubmitFormData = {
	termsAccepted: boolean;
};

const ReviewDepositUI: React.FC<SubmitPageProps> = ({
	amount,
	onSubmit,
	onBack,
	selectedCard,
	promo,
	transactionPreview,
}) => {
	const {
		requested_amount_dollars,
		requested_amount_cents,
		service_fee_percentage,
		effective_service_fee_percentage,
		service_fee_amount_dollars,
		service_fee_amount_cents,
		service_fee_before_discount_amount_dollars,
		discount_amount_dollars,
		amount_after_fee_dollars,
		discount_name,
	} = transactionPreview;

	const hasPromo = discount_amount_dollars > 0;
	const promoLabel = promo || discount_name;
	// Percent promos set the fee rate, so show the API's exact rate; dollar promos keep the base rate and the
	// rate is derived from the discounted fee.
	const hasDiscountedRate =
		effective_service_fee_percentage !== undefined &&
		effective_service_fee_percentage !== service_fee_percentage;
	const discountedFeePercentage = hasDiscountedRate
		? effective_service_fee_percentage
		: requested_amount_cents > 0
			? (service_fee_amount_cents / requested_amount_cents) * 100
			: requested_amount_dollars > 0
				? (service_fee_amount_dollars / requested_amount_dollars) * 100
				: service_fee_percentage;

	const navigate = useNavigate();

	const form = useForm<SubmitFormData>({
		mode: "onChange",
		resolver: zodResolver(submitSchema),
		defaultValues: { termsAccepted: false },
	});

	const { control, handleSubmit, watch } = form;
	const termsAccepted = watch("termsAccepted");

	const handleFormSubmit = () => {
		if (termsAccepted) {
			onSubmit();
		}
	};

	function formatPercent(pct: number) {
		return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(pct)}%`;
	}

	return (
		<div className="h-full flex flex-col justify-between">
			<div>
				<DepositHeader
					onClose={() => navigate({ to: "/main/deposit" })}
					onBack={onBack}
					title="Confirm Details"
				/>
				<div>
					<div className="text-center">
						<p className="text-sm font-medium text-textDark opacity-80">
							Please take a moment to ensure the
						</p>
						<p className="text-sm font-medium text-textDark opacity-80">
							amounts and credit card details are correct.
						</p>
					</div>
					<div className="py-6">
						<h1 className="text-4xl text-center font-bold text-textDark">
							{currencyFormatter.format(amount)}
						</h1>
						<p className="text-lg mt-4  text-center font-medium opacity-40 text-textDark">
							Amount to be charged on card
						</p>
					</div>
					<div className="w-full mb-4">
						<PaymentCard card={selectedCard} />
					</div>
					{hasPromo && (
						<div className="w-full mb-4 rounded-3xl border border-gray-200 bg-white p-4">
							<div className="flex items-start gap-3">
								<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
									<CheckIcon
										className="h-4 w-4 text-green-600"
										strokeWidth={2.5}
									/>
								</div>
								<div className="flex-1 min-w-0 pt-0.5">
									<p className="text-base font-bold text-textDark mt-1 tracking-tight">
										{promoLabel}
									</p>
									<p className="text-sm text-textLight mt-1.5">
										You're saving{" "}
										<span className="font-semibold text-textDark">
											{currencyFormatter.format(discount_amount_dollars)}
										</span>{" "}
										on your service fee
									</p>
								</div>
							</div>
						</div>
					)}
					<div className="w-full">
						<div className="flex justify-between py-2 border-b border-gray-200">
							<p className="text-sm font-medium text-textDark opacity-60">
								Base amount
							</p>
							<p className="text-sm font-bold text-textDark">
								{currencyFormatter.format(requested_amount_dollars)}
							</p>
						</div>
						<div className="flex justify-between py-2 border-b border-gray-200">
							<p className="text-sm font-medium text-textDark opacity-60">
								Fee rate
							</p>
							{hasPromo ? (
								<div className="text-sm font-bold text-textDark flex items-center gap-2">
									<span className="text-textLight line-through font-medium">
										{formatPercent(service_fee_percentage)}
									</span>
									<span>{formatPercent(discountedFeePercentage)}</span>
								</div>
							) : (
								<p className="text-sm font-bold text-textDark">
									{formatPercent(service_fee_percentage)}
								</p>
							)}
						</div>
						<div className="flex justify-between py-2 border-b border-gray-200">
							<p className="text-sm font-medium text-textDark opacity-60">
								Service fee
							</p>
							{hasPromo ? (
								<div className="text-sm font-bold text-textDark flex items-center gap-2">
									<span className="text-textLight line-through font-medium">
										{currencyFormatter.format(
											service_fee_before_discount_amount_dollars,
										)}
									</span>
									<span>
										{currencyFormatter.format(service_fee_amount_dollars)}
									</span>
								</div>
							) : (
								<p className="text-sm font-bold text-textDark">
									{currencyFormatter.format(service_fee_amount_dollars)}
								</p>
							)}
						</div>
						<div className="flex justify-between py-2 border-b border-gray-200">
							<p className="text-sm font-medium text-textDark opacity-60">
								Total available funds after fees
							</p>
							<p className="text-sm font-bold text-textDark">
								{currencyFormatter.format(amount_after_fee_dollars)}
							</p>
						</div>
					</div>
				</div>
			</div>
			<div>
				<div>
					<Form {...form}>
						<form className="" onSubmit={handleSubmit(handleFormSubmit)}>
							<div className="flex items-center gap-2 mt-4">
								<FormField
									control={control}
									name="termsAccepted"
									rules={{ required: true }}
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-row items-center space-x-2 space-y-0">
												<FormControl>
													<Checkbox
														checked={field.value}
														onCheckedChange={field.onChange}
													/>
												</FormControl>
												<FormLabel className="text-xs font-medium text-textDark mb-0">
													<span className="opacity-80 ">
														By submitting payment below I have read and agree to{" "}
													</span>
													<span className="font-bold text-textDark">
														<a
															target="_blank"
															rel="noreferrer"
															className={buttonVariants({
																variant: "link",
																size: "compact",
															})}
															href={getTermsOfServiceUrl()}
														>
															Terms & Conditions.
														</a>
													</span>
												</FormLabel>
											</div>
											<FormMessage className="ml-8" />
										</FormItem>
									)}
								/>
							</div>
							<Button
								className="w-full shadow-none mt-4"
								variant={"secondary"}
								type="submit"
							>
								<p>Submit Payment</p>
								<ArrowRightIcon className="w-4 h-4" />
							</Button>
						</form>
					</Form>
				</div>
			</div>
		</div>
	);
};

export default ReviewDepositUI;
